import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';

import { GroupedBarChart } from 'pages/FinanceDashboard/charts';

import { TCalendarDay, TCalendarView, TONE, fmtMoney } from '../shared';

export interface ICalendarChartsProps {
  /** Days currently in scope (the selected day, the week, or the month). */
  scopeDays: TCalendarDay[];
  /** Active cockpit view — picks day/week/month chart framing. */
  view: TCalendarView;
}

/** One assembled income-vs-expense series ready for GroupedBarChart. */
interface IChartSeries {
  labels: string[];
  income: number[];
  expense: number[];
}

/** A day's income/expense totals, nullish-safe. */
const dayIncome = (d: TCalendarDay): number => d.incomeTotalPln || 0;
const dayExpense = (d: TCalendarDay): number => d.expenseTotalPln || 0;

/** A day reads as a cash-gap day when the backend flags its tone red. */
const isGapDay = (d: TCalendarDay): boolean =>
  d.tone === 'red' || d.tone === 'danger' || (d.endBalancePln || 0) < 0;

/**
 * WEEK framing — one bar pair per ISO weekday (Mon..Sun), labelled by short
 * weekday name. Days are bucketed by their own date so gaps in `scopeDays`
 * still line up under the right weekday.
 */
const buildWeekSeries = (days: TCalendarDay[]): IChartSeries => {
  // isoWeekday(): Mon=1..Sun=7 → index 0..6
  const income = Array<number>(7).fill(0);
  const expense = Array<number>(7).fill(0);
  days.forEach((d) => {
    if (!d.date) return;
    const idx = moment(d.date).isoWeekday() - 1;
    if (idx < 0 || idx > 6) return;
    income[idx] += dayIncome(d);
    expense[idx] += dayExpense(d);
  });
  const labels = Array.from({ length: 7 }, (_, i) =>
    moment()
      .isoWeekday(i + 1)
      .format('dd'),
  );
  return { labels, income, expense };
};

/**
 * HORIZON framing (month / six-weeks) — one bar pair per ISO week, bucketing
 * the GIVEN days by their own `startOf('isoWeek')`. Anchor-independent: it
 * charts exactly the weeks present in `days` (the full fetched range), so it
 * stays correct as the user pages forward/back — no dependency on "today".
 * Labelled by each week's Monday (DD.MM), sorted ascending.
 */
const buildWeeklyBuckets = (days: TCalendarDay[]): IChartSeries => {
  const buckets = new Map<string, { income: number; expense: number }>();
  days.forEach((d) => {
    if (!d.date) return;
    const wk = moment(d.date).startOf('isoWeek').format('YYYY-MM-DD');
    const acc = buckets.get(wk) || { income: 0, expense: 0 };
    acc.income += dayIncome(d);
    acc.expense += dayExpense(d);
    buckets.set(wk, acc);
  });
  const weekStarts = Array.from(buckets.keys()).sort();
  return {
    labels: weekStarts.map((w) => moment(w).format('DD.MM')),
    income: weekStarts.map((w) => buckets.get(w)?.income || 0),
    expense: weekStarts.map((w) => buckets.get(w)?.expense || 0),
  };
};

/**
 * DAY framing — the single selected day (or the only scope day) rendered as one
 * income-vs-expense bar pair, labelled DD.MM.
 */
const buildDaySeries = (days: TCalendarDay[]): IChartSeries => {
  const dated = days.filter((d) => !!d.date);
  return {
    labels: dated.map((d) => moment(d.date as string).format('DD.MM')),
    income: dated.map(dayIncome),
    expense: dated.map(dayExpense),
  };
};

/* ----- compact, right-rail-sized card (local; height ~150px) ------------- *
 * Reuses the AnalyticsPrimitives ChartCard visual tokens but with a shorter
 * chart body so the card fits the dense right rail. Kept local (not editing
 * the shared molecule) per the v3 contract.                                  */

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  gap: 10px;
`;

const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.span<{ color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 2px;
  display: inline-block;
  flex: 0 0 auto;
  background: ${({ color }) => color};
`;

const CardMeta = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
  text-align: right;
`;

/** The compact chart body — ~150px tall vs the shared card's 200px. */
const ChartWrap = styled.div`
  position: relative;
  height: 150px;
`;

const FooterNote = styled.div`
  font-size: 11px;
  margin-top: 6px;
`;

/**
 * Income-vs-expense GroupedBarChart in a compact, right-rail card, framed per
 * view: single day, 7-day week, 6-week horizon (per-week buckets), or
 * month-by-week/day.
 *
 * Pure presentational: every series is derived from `scopeDays`. When the scope
 * runs a cash gap (total expense > total income, or any day's tone is red) the
 * card is emphasised in red (danger dot + footer note showing the minimum
 * end-balance) to flag a кассовый разрыв.
 */
export const CalendarCharts = memo<ICalendarChartsProps>(
  ({ scopeDays, view }) => {
    const { t } = useTranslation();

    const series = useMemo<IChartSeries>(() => {
      if (view === 'month' || view === 'sixWeeks')
        return buildWeeklyBuckets(scopeDays);
      if (view === 'today') return buildDaySeries(scopeDays);
      return buildWeekSeries(scopeDays);
    }, [scopeDays, view]);

    const { totalIncome, totalExpense, minEndBalance, hasGap } = useMemo(() => {
      const inc = scopeDays.reduce((s, d) => s + dayIncome(d), 0);
      const exp = scopeDays.reduce((s, d) => s + dayExpense(d), 0);
      const ends = scopeDays
        .map((d) => d.endBalancePln)
        .filter((v): v is number => v !== null && v !== undefined);
      // Include the scope's opening balance — the backend seeds min_balance
      // with OPENING, so a range that starts negative is a shortfall even if
      // every day ends positive (keeps the dot in sync with SaldoBlock).
      const opening = scopeDays.length ? scopeDays[0].openingBalancePln : null;
      const mins =
        opening !== null && opening !== undefined ? [opening, ...ends] : ends;
      const minBal = mins.length ? Math.min(...mins) : null;
      return {
        totalIncome: inc,
        totalExpense: exp,
        minEndBalance: minBal,
        hasGap:
          exp > inc ||
          (minBal !== null && minBal < 0) ||
          scopeDays.some(isGapDay),
      };
    }, [scopeDays]);

    const title = useMemo(() => {
      switch (view) {
        case 'month':
          return t('pages.paymentCalendar.charts.monthTitle');
        case 'sixWeeks':
          return t('pages.paymentCalendar.charts.sixWeeksTitle');
        case 'today':
          return t('pages.paymentCalendar.charts.dayTitle');
        default:
          return t('pages.paymentCalendar.charts.weekTitle');
      }
    }, [view, t]);

    const dotColor = hasGap ? TONE.danger : TONE.ok;

    const meta = `${t('pages.paymentCalendar.charts.incomeSeries')}: ${fmtMoney(
      totalIncome,
    )} · ${t('pages.paymentCalendar.charts.expenseSeries')}: ${fmtMoney(
      totalExpense,
    )}`;

    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <Dot color={dotColor} />
            {title}
          </CardTitle>
          <CardMeta>{meta}</CardMeta>
        </CardHeader>
        <ChartWrap>
          <GroupedBarChart
            labels={series.labels}
            aValues={series.income}
            bValues={series.expense}
            aLabel={t('pages.paymentCalendar.charts.incomeSeries')}
            bLabel={t('pages.paymentCalendar.charts.expenseSeries')}
            aColor={TONE.ok}
            bColor={TONE.danger}
          />
        </ChartWrap>
        {hasGap && (
          <FooterNote style={{ color: TONE.danger, fontWeight: 600 }}>
            {t('pages.paymentCalendar.summaryV2.minBalance')}:{' '}
            {fmtMoney(minEndBalance)}
          </FooterNote>
        )}
      </Card>
    );
  },
);
